import { IStepGeometry, IStringerType } from '@stair-craft/common/util';

import { TREAD_SIDE_OVERHANG } from '../../constants';
import { IPlanPoint } from '../shared-plan-geometry/plan-geometry';

type IWinderCorners = NonNullable<IStepGeometry['winderCorners']>;

/**
 * Moves a leading corner forward along its side edge so the nose overhangs the leading edge by
 * `nose` measured square to it. Running along the side edge keeps the nose on the wall line (L/U)
 * and on the chord that touches the pole (spiral).
 */
function extendAlongEdge(corner: IPlanPoint, next: IPlanPoint, otherCorner: IPlanPoint, nose: number): IPlanPoint {
    const ex: number = corner.x - next.x;
    const ez: number = corner.z - next.z;
    const edgeLen: number = Math.hypot(ex, ez);
    const lx: number = otherCorner.x - corner.x;
    const lz: number = otherCorner.z - corner.z;
    const leadLen: number = Math.hypot(lx, lz);
    if (edgeLen < 1e-9 || leadLen < 1e-9) {
        return corner;
    }
    // Sine of the angle between the side edge and the leading edge.
    const sine: number = Math.abs(ex * lz - ez * lx) / (edgeLen * leadLen);
    const reach: number = nose / Math.max(0.3, sine);
    return { x: corner.x + (ex / edgeLen) * reach, z: corner.z + (ez / edgeLen) * reach };
}

/**
 * A winder tread in plan: outer edge from the (nosed) leading corner to the trailing one, then
 * the inner edge back, without repeated points. The order is the corners' own; the caller fixes
 * the winding it needs.
 */
export function winderTreadOutline(corners: IWinderCorners, nose: number): IPlanPoint[] {
    let { outerBack, innerBack } = corners;
    if (nose > 0) {
        outerBack = extendAlongEdge(corners.outerBack, corners.outerMidPoints?.[0] ?? corners.outerFront, corners.innerBack, nose);
        innerBack = extendAlongEdge(corners.innerBack, corners.innerMidPoints?.[0] ?? corners.innerFront, corners.outerBack, nose);
    }
    const outer: IPlanPoint[] = [outerBack, ...(corners.outerMidPoints ?? []), corners.outerFront];
    // The inner edge runs back from the trailing corner to the leading one.
    const innerBackward: IPlanPoint[] = [corners.innerFront, ...[...(corners.innerMidPoints ?? [])].reverse(), innerBack];
    const perimeter: IPlanPoint[] = [...outer, ...innerBackward];
    return perimeter.filter((point: IPlanPoint, index: number) => {
        const previous: IPlanPoint = perimeter[(index + perimeter.length - 1) % perimeter.length];
        return index === 0 || Math.abs(point.x - previous.x) > 0.01 || Math.abs(point.z - previous.z) > 0.01;
    });
}

/** How far a tread reaches past the stair edge: only flight treads on kosour boards do. */
export function treadSideOverhang(step: IStepGeometry, stringerType: IStringerType): number {
    return stringerType === 'kosour' && !step.isLanding && !step.isWinder ? TREAD_SIDE_OVERHANG : 0;
}

/**
 * The tread in plan exactly as the 3D draws it: a winder's polygon, or the rectangle of a flight
 * tread or landing — nose ahead of the leading edge, side overhang on kosour boards — turned
 * about its centre by `rotation`.
 */
export function treadFootprint(step: IStepGeometry, stringerType: IStringerType): IPlanPoint[] {
    if (step.isWinder && step.winderCorners) {
        return winderTreadOutline(step.winderCorners, step.noseLength);
    }
    const overhang: number = treadSideOverhang(step, stringerType);
    const cx: number = step.position.x + step.treadWidth / 2;
    const cz: number = step.position.z + (step.treadDepth - step.noseLength) / 2;
    const halfW: number = step.treadWidth / 2 + overhang;
    const halfD: number = (step.treadDepth + step.noseLength) / 2;
    const angle: number = (step.rotation * Math.PI) / 180;
    const cos: number = Math.cos(angle);
    const sin: number = Math.sin(angle);
    // Same turn as Object3D.rotateY: x' = x·cos + z·sin, z' = −x·sin + z·cos.
    return [
        [-halfW, -halfD],
        [halfW, -halfD],
        [halfW, halfD],
        [-halfW, halfD],
    ].map(([lx, lz]: number[]) => ({ x: cx + lx * cos + lz * sin, z: cz - lx * sin + lz * cos }));
}

/** Points this close to an outline edge count as lying on it (mm). */
const ON_EDGE: number = 1e-6;

function onSegment(a: IPlanPoint, b: IPlanPoint, point: IPlanPoint): boolean {
    const dx: number = b.x - a.x;
    const dz: number = b.z - a.z;
    const lengthSq: number = dx * dx + dz * dz;
    const t: number = lengthSq < 1e-12 ? 0 : Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.z - a.z) * dz) / lengthSq));
    return Math.hypot(a.x + t * dx - point.x, a.z + t * dz - point.z) <= ON_EDGE;
}

/**
 * Whether a point lies inside a polygon (either winding), edges included. The outline may be
 * concave: a U-turn winder with its nose carried along both sides has a notch at the inner corner.
 */
export function insideOutline(polygon: readonly IPlanPoint[], point: IPlanPoint): boolean {
    let inside: boolean = false;
    for (let i: number = 0; i < polygon.length; i++) {
        const a: IPlanPoint = polygon[i];
        const b: IPlanPoint = polygon[(i + polygon.length - 1) % polygon.length];
        if (onSegment(a, b, point)) {
            return true;
        }
        if (a.z > point.z !== b.z > point.z && point.x < ((b.x - a.x) * (point.z - a.z)) / (b.z - a.z) + a.x) {
            inside = !inside;
        }
    }
    return inside;
}
